if (!customElements.get('content-accordion')) {
  class Accordion extends HTMLElement {
    constructor() {
      super();
      this.observer = new MutationObserver(() => this.restoreAccordionState());
      this.handleResize = this.debounce(() => this.updateOpenAccordions(), 100);
    }
    
    connectedCallback() {
      // Initialize manually opened accordions. Keeps animation from breaking on 1st close
      this.querySelectorAll('[data-accordion-toggle].open').forEach(toggle => {
        const id = toggle.dataset.accordionToggle;
        const content = this.querySelector(`[data-accordion-content="${id}"]`);
        if (content && !content.style.height) {
          content.style.height = `${content.scrollHeight}px`;
        }
      });

      this.observer.observe(this, { childList: true, subtree: true });
      window.addEventListener('resize', this.handleResize);
      
      this.addEventListener('click', (e) => {
        const clickedElement = e.target.closest('[data-accordion-toggle]');
        const independent = clickedElement?.hasAttribute("data-independent");
        if (!clickedElement) return;
          
        const id = clickedElement.dataset.accordionToggle;
        const content = this.querySelector(`[data-accordion-content="${id}"]`);
        const rotatedEl = clickedElement.querySelector('[data-rotate-active]');
        const rotation = rotatedEl?.dataset.rotateActive || 0;
        
        const activeAccordions = JSON.parse(sessionStorage.getItem('activeAccordions') || '[]');
        
        if (content.clientHeight > 0) {
          content.style.height = 0;
          clickedElement.classList.remove('open');
          sessionStorage.setItem('activeAccordions', JSON.stringify(activeAccordions.filter(item => item !== id)));
          
          if (!rotatedEl) return;
          rotatedEl.style.transform = null;
          
          return;
        }
        
        if (!independent) {
          [...this.querySelectorAll('[data-accordion-content]')]
            .filter((x) => !x.isSameNode(content))
            .forEach((x) => {
              x.style.transition = '';
              x.style.height = 0;
            });
          [...this.querySelectorAll('[data-accordion-toggle]')]
            .filter((x) => !x.isSameNode(clickedElement))
            .forEach((x) => {
              if (!x.querySelector('[data-rotate-active]')) return;
              x.classList.remove('open');
              const rotated = x.querySelector('[data-rotate-active]');
              rotated.style.transition = '';
              rotated.style.transform = null;
            });
        }
        
        content.style.transition = '';
        if (rotatedEl) rotatedEl.style.transition = '';
        
        content.style.height = `${content.scrollHeight || 0}px`;   
        clickedElement.classList.add('open');
        
        if (!activeAccordions.includes(id)) {
          activeAccordions.push(id);
        }
        sessionStorage.setItem('activeAccordions', JSON.stringify(activeAccordions));
        
        if (!rotatedEl) return;
        rotatedEl.style.transform = `rotate(${rotation}deg)`;
      });
    }
    
    disconnectedCallback() {
      this.observer.disconnect();
      window.removeEventListener('resize', this.handleResize);
    }
    
    updateOpenAccordions() {
      this.querySelectorAll('[data-accordion-toggle].open').forEach(toggle => {
        const id = toggle.dataset.accordionToggle;
        const content = this.querySelector(`[data-accordion-content="${id}"]`);
        if (content && content.style.height && content.style.height !== '0px') {
          content.style.transition = 'none';
          content.style.height = `${content.scrollHeight}px`;
          setTimeout(() => {
            content.style.transition = '';
          }, 10);
        }
      });
    }
    
    debounce(func, wait) {
      let timeout;
      return function() {
        const context = this, args = arguments;
        clearTimeout(timeout);
        timeout = setTimeout(() => {
          func.apply(context, args);
        }, wait);
      };
    }
    
    restoreAccordionState() {
      const animatedElements = this.querySelectorAll('[data-accordion-content], [data-rotate-active]');
      animatedElements.forEach((el) => {
        el.style.transition = 'none'
      });
      
      const savedIds = JSON.parse(sessionStorage.getItem('activeAccordions') || '[]');
      
      savedIds.forEach(savedId => {
        const content = this.querySelector(`[data-accordion-content="${savedId}"]`);
        if (content) {
          content.style.height = '';
        }
      });
      
      savedIds.forEach(savedId => {
        const toggle = this.querySelector(`[data-accordion-toggle="${savedId}"]`);
        const content = this.querySelector(`[data-accordion-content="${savedId}"]`);
        if (toggle && content) {
          toggle.classList.add('open');
          
          const rotatedEl = toggle.querySelector('[data-rotate-active]');
          if (rotatedEl) {
            const rotation = rotatedEl.dataset.rotateActive || 0;
            rotatedEl.style.transform = `rotate(${rotation}deg)`;
          }
        }
      });
      
      setTimeout(() => {
        animatedElements.forEach((el) => {
          el.style.transition = '';
        });
      });
    }
  }
  
  customElements.define('content-accordion', Accordion);
}

/*
EXAMPLE USAGE
*
*
* 
* 
* 
<content-accordion class="px-10"> {% for link in menu.links %} 
  <button data-accordion-toggle="{{ link.handle }}-{{ forloop.index }}" class="flex items-center justify-between text-xl pt-7 w-full">
    {{ link.title }}
    <div aria-hidden="true" class="w-3.5 h-3.5 relative">
      <span class="absolute top-1/2 transform translate-y-1/2 left-0 w-full block border border-current"></span>
      <span class="absolute block w-full h-0.5 top-1/2 transform translate-y-1/2">
        <span data-rotate-active class="transform rotate-90 transition duration-200 left-0 w-full block border border-current"></span>
      </span>
    </div>
  </button>
  <div class="overflow-hidden transition-all duration-500" style="height: 0;" data-accordion-content="{{ link.handle }}-{{ forloop.index }}">
    <ul class="pt-7 flex flex-col gap-2 text-secondaryDark"> {% for child_link in link.links %} <li>
        <a href="{{ child_link.url }}">{{ child_link.title }}</a>
      <li> {% endfor %}
    </ul>
    <!-- optional submenu (example usage on collection filter drawer) -->
    <div class="overflow-hidden transition-all duration-500" style="height: 0;" data-accordion-submenu-content="submenu-{{ filter.label }}">
      <ul class="flex flex-col gap-2 text-small">
        <li class="relative flex items-center pb-3 pt-3 {% unless forloop.last %} border-b {% endunless %}"> Some additional content in a sub-accordion
        <li>
      </ul>
    </div>
    <button class="py-5 text-secondaryDark" data-accordion-submenu-toggle="submenu-{{ filter.label }}"> + MORE {{ filter.label | upcase }}S </button>
    <!-- end optional submenu (example usage on collection filter drawer) -->
  </div> {% endfor %}
</content-accordion>

data-independant on the button will keep accordions from collapsing when a new one is opened
*/